import * as React from 'react';
import { Snackbar, Paper, Box, Typography, IconButton } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { useI18n } from '../../i18n/I18nProvider';
import fancyToastI18n from '../../i18n/components/common/FancyToast';

// palette
const BLUE = '#0466c8';
const BLUE_DARK = '#004890';

const iconMap = {
  success: CheckCircleRoundedIcon,
  celebration: CelebrationRoundedIcon,
};

/**
 * Props:
 *  - open
 *  - onClose
 *  - kind?: 'success' | 'celebration'
 *  - title?: string        // prioritaire sur la traduction
 *  - subtitle?: string     // prioritaire sur la traduction
 *  - titleKey?: string     // ex: 'fancyToast.login.title'
 *  - subtitleKey?: string  // ex: 'fancyToast.login.subtitle'
 *  - autoHideDuration?: number
 *  - anchorOrigin?: { vertical: 'top'|'bottom', horizontal: 'left'|'center'|'right' }
 */
export default function FancyToast({
  open,
  onClose,
  kind = 'success',
  title,
  subtitle = '',
  titleKey,
  subtitleKey,
  autoHideDuration = 1500,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
}) {
  // i18n
  const { t, register } = useI18n();
  React.useEffect(() => { register('fancyToast', fancyToastI18n); }, [register]);

  const Icon = iconMap[kind] || iconMap.success;

  // 1) si props title/subtitle sont fournis -> on les utilise
  // 2) sinon si des keys sont fournies -> on utilise t(key)
  // 3) sinon on tombe sur une valeur par défaut en fonction du "kind"
  const fallbackTitle =
    kind === 'celebration' ? t('fancyToast.default.celebrationTitle') : t('fancyToast.default.successTitle');
  const fallbackSubtitle = t('fancyToast.default.subtitle');

  const finalTitle = title ?? (titleKey ? t(titleKey) : fallbackTitle);
  const finalSubtitle = subtitle !== '' ? subtitle : (subtitleKey ? t(subtitleKey) : fallbackSubtitle);

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      autoHideDuration={autoHideDuration}
      anchorOrigin={anchorOrigin}
      ClickAwayListenerProps={{ mouseEvent: false, touchEvent: false }}
    >
      <Paper
        elevation={6}
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 3,
          color: '#fff',
          minWidth: 320,
          maxWidth: 420,
          backdropFilter: 'blur(6px)',
          background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARK} 100%)`,
          boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box
            sx={{
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)',
            }}
          >
            <Icon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.15 }}>
              {finalTitle}
            </Typography>
            {finalSubtitle ? (
              <Typography sx={{ opacity: 0.95, mt: 0.25, fontSize: 13.5 }}>
                {finalSubtitle}
              </Typography>
            ) : null}
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              '&:hover': { color: '#fff' },
            }}
            aria-label={t('fancyToast.a11y.close')}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Snackbar>
  );
}